/**
 * ReprocessarDialog (ADR-036, spec reprocessamento-simples, design §5) — o diálogo único de reprocessamento, aberto
 * pela ficha (uma visita), pela lista (o filtro APLICADO — o conjunto é exatamente o da tela) e pelo painel (uma
 * pendência). Antes de confirmar mostra a estimativa:
 *  - "Conferir de novo": aplica as regras e bases de hoje à leitura que já existe, sem custo (modo `revalidar`);
 *  - "Ler a imagem de novo": só quando há visitas lidas com um prompt antigo — quantas, quantas ficam de fora (já na
 *    versão atual — RN-GLOBAL-008) e o custo estimado; acima do limite (`confirmarAcimaUsd`), a caixa "Entendo o
 *    custo" é obrigatória.
 * Sem versão de prompt nem identificadores: o "ler de novo" é sempre na versão vigente. Depois do 202, o aviso
 * "Reprocessando N visitas" com o link para acompanhar. 403/422 aparecem no próprio diálogo. Nenhum dado pessoal.
 * ADR-038: as visitas sem imagem (da planilha) não são lidas de novo — "N sem imagem ficam de fora"; a visita sem
 * imagem só pode ser conferida de novo.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCriarReprocessamento, useEstimativaReprocessamento } from '../hooks/usePainel';
import { ApiError } from '../services/apiClient';
import type { FiltroVisitasQuery, PendenciaVisita } from '../services/consulta-types';
import type {
  CriarReprocessamentoInput,
  CriarReprocessamentoResponse,
  CriterioEstimativa,
  ModoReprocessamento,
} from '../services/painel-types';
import { formatarUsd } from '../pages/LotesPage';

/** O que reprocessar: a visita da ficha, o filtro aplicado da lista (com o total dela) ou uma pendência do painel. */
export type AlvoReprocessamento =
  | { readonly tipo: 'visita'; readonly visitaId: string }
  | { readonly tipo: 'filtro'; readonly filtro: FiltroVisitasQuery; readonly total: number }
  | { readonly tipo: 'pendencia'; readonly pendencia: PendenciaVisita };

export interface ReprocessarDialogProps {
  readonly alvo: AlvoReprocessamento;
  readonly onFechar: () => void;
  /** Chamado depois do 202 (a ficha acompanha o andamento, a lista atualiza…). */
  readonly onDisparado?: (resposta: CriarReprocessamentoResponse) => void;
}

const numero = new Intl.NumberFormat('pt-BR');
const visitas = (n: number): string => `${numero.format(n)} ${n === 1 ? 'visita' : 'visitas'}`;
const semFiltro = (filtro: FiltroVisitasQuery): boolean => Object.keys(filtro).length === 0;

/** O nome da ação sobre o filtro aplicado da lista — o mesmo no botão da lista e no título do diálogo. */
export function rotuloReprocessarFiltro(filtro: FiltroVisitasQuery, total: number): string {
  if (semFiltro(filtro)) return `Reprocessar todas as ${visitas(total)}`;
  return total === 1 ? 'Reprocessar 1 visita' : `Reprocessar estas ${visitas(total)}`;
}

function titulo(alvo: AlvoReprocessamento): string {
  if (alvo.tipo === 'visita') return 'Reprocessar esta visita';
  if (alvo.tipo === 'pendencia') {
    return alvo.pendencia === 'prompt_antigo'
      ? 'Visitas lidas com um prompt antigo'
      : 'Visitas conferidas antes da mudança de parâmetros ou bases';
  }
  return rotuloReprocessarFiltro(alvo.filtro, alvo.total);
}

function criterioDaEstimativa(alvo: AlvoReprocessamento): CriterioEstimativa {
  if (alvo.tipo === 'visita') return { visitaId: alvo.visitaId };
  if (alvo.tipo === 'pendencia') return { filtro: { pendencia: alvo.pendencia } };
  return { filtro: alvo.filtro };
}

/** O corpo do POST: o critério e a origem; sem versão (o "ler de novo" é na vigente). */
function corpo(alvo: AlvoReprocessamento, modo: ModoReprocessamento): CriarReprocessamentoInput {
  if (alvo.tipo === 'visita') return { modo, origem: 'ficha', visitaId: alvo.visitaId };
  if (alvo.tipo === 'pendencia') return { modo, origem: 'painel', pendencia: alvo.pendencia };
  return { modo, origem: 'lista', filtroVisitas: alvo.filtro };
}

function mensagemDeErro(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) return 'Seu perfil não tem permissão para reprocessar.';
  if (err instanceof ApiError && err.status === 422) return 'Nenhuma visita para reprocessar com esse critério.';
  return 'Não foi possível disparar o reprocessamento. Tente novamente.';
}

export function ReprocessarDialog({ alvo, onFechar, onDisparado }: ReprocessarDialogProps): JSX.Element {
  const [criterio] = useState(() => criterioDaEstimativa(alvo));
  const { estimativa: e, carregando, erro: erroEstimativa } = useEstimativaReprocessamento(criterio);
  const { criar, enviando } = useCriarReprocessamento();
  const [escolha, setEscolha] = useState<ModoReprocessamento | null>(null);
  const [entendoCusto, setEntendoCusto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [disparado, setDisparado] = useState<CriarReprocessamentoResponse | null>(null);

  const podeLer = (e?.lerDeNovo.visitas ?? 0) > 0;
  // Padrão: "conferir de novo" (sem custo); a sugestão de prompt antigo abre em "ler de novo".
  const padrao: ModoReprocessamento = alvo.tipo === 'pendencia' && alvo.pendencia === 'prompt_antigo' && podeLer ? 'reler' : 'revalidar';
  const modo: ModoReprocessamento = escolha === 'reler' && !podeLer ? 'revalidar' : (escolha ?? padrao);
  const custo = e?.lerDeNovo.custoEstimadoUsd ?? null;
  const semImagem = e?.lerDeNovo.semImagem ?? 0;
  const precisaEntender = modo === 'reler' && custo !== null && e !== undefined && custo > e.confirmarAcimaUsd;

  async function confirmar(): Promise<void> {
    setErro(null);
    try {
      const resposta = await criar(corpo(alvo, modo));
      setDisparado(resposta);
      onDisparado?.(resposta);
    } catch (err) {
      setErro(mensagemDeErro(err));
    }
  }

  const nome = titulo(alvo);
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={nome}>
      <div className="modal">
        <h2>{nome}</h2>
        {carregando && <p className="muted">Calculando…</p>}
        {erroEstimativa && <p className="notice">Não foi possível calcular a estimativa. Tente de novo.</p>}

        {disparado && (
          <p className="notice notice-info">
            {`Reprocessando ${visitas(disparado.estimativaAfetadas)}.`}{' '}
            <Link to={`/reprocessamentos/${disparado.reprocessamentoId}`}>Acompanhar</Link>
          </p>
        )}

        {e && !disparado && e.total === 0 && <p>Nenhuma visita para reprocessar com esse critério.</p>}

        {e && !disparado && e.total > 0 && (
          <>
            {alvo.tipo === 'filtro' && semFiltro(alvo.filtro) && (
              <p className="notice notice-info">
                Nenhum filtro aplicado: todas as visitas do acervo ({visitas(e.total)}).
              </p>
            )}
            <fieldset className="opcoes-reprocessamento">
              <legend>O que fazer</legend>
              <label className="opcao">
                <input
                  type="radio"
                  name="modo-reprocessamento"
                  checked={modo === 'revalidar'}
                  onChange={() => setEscolha('revalidar')}
                />{' '}
                <strong>Conferir de novo</strong>
              </label>
              <p className="muted opcao-descricao">
                Aplica as regras e bases de hoje à leitura que já existe. Sem custo.
              </p>

              {podeLer ? (
                <>
                  <label className="opcao">
                    <input
                      type="radio"
                      name="modo-reprocessamento"
                      checked={modo === 'reler'}
                      onChange={() => setEscolha('reler')}
                    />{' '}
                    <strong>Ler a imagem de novo</strong>
                  </label>
                  <p className="muted opcao-descricao">
                    {e.lerDeNovo.visitas === 1
                      ? '1 visita lida com um prompt antigo será lida de novo'
                      : `${numero.format(e.lerDeNovo.visitas)} visitas lidas com um prompt antigo serão lidas de novo`}{' '}
                    com a versão atual (<span className="mono">{e.versaoVigente}</span>).
                    {e.lerDeNovo.jaNaVigente > 0 && ` ${numero.format(e.lerDeNovo.jaNaVigente)} já estão na versão atual e ficam de fora.`}
                    {semImagem > 0 && ` ${numero.format(semImagem)} sem imagem ficam de fora.`}
                  </p>
                  <p className="muted opcao-descricao">
                    {custo === null
                      ? 'Custo: sem estimativa de custo (ainda não há leituras com custo registrado).'
                      : `Custo estimado: ${formatarUsd(custo)}.`}
                  </p>
                </>
              ) : (
                <p className="muted">
                  {textoLerNaoSeAplica(alvo.tipo === 'visita', semImagem, e.total)}
                </p>
              )}
            </fieldset>

            {precisaEntender && custo !== null && (
              <label className="confirmacao-custo">
                <input type="checkbox" checked={entendoCusto} onChange={(ev) => setEntendoCusto(ev.target.checked)} />
                {`Entendo o custo de ${formatarUsd(custo)}`}
              </label>
            )}
          </>
        )}

        {erro && <p className="notice">{erro}</p>}

        <div className="row-actions">
          {e && !disparado && e.total > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={enviando || (precisaEntender && !entendoCusto)}
              onClick={() => void confirmar()}
            >
              Confirmar
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onFechar}>
            {disparado ? 'Fechar' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Por que "Ler a imagem de novo" não se aplica: já na versão atual, ou sem imagem (ADR-038). */
function textoLerNaoSeAplica(umaVisita: boolean, semImagem: number, total: number): string {
  if (umaVisita) {
    return semImagem > 0
      ? 'Ler a imagem de novo não se aplica: esta visita não tem imagem (veio da planilha de instalações).'
      : 'Ler a imagem de novo não se aplica: esta visita já foi lida com a versão atual do prompt.';
  }
  if (semImagem >= total) {
    return 'Ler a imagem de novo não se aplica: nenhuma tem imagem (vieram da planilha de instalações).';
  }
  return semImagem > 0
    ? `Ler a imagem de novo não se aplica: as com imagem já foram lidas com a versão atual do prompt (${numero.format(semImagem)} sem imagem ficam de fora).`
    : 'Ler a imagem de novo não se aplica: todas já foram lidas com a versão atual do prompt.';
}
